import { Card } from '../ui/Card.jsx';
import { Button } from '../ui/Button.jsx';
import { useEffect, useRef } from 'react';
import { useAudio } from '../../hooks/useAudio.js';
import { getAvailableTasks } from '../../config/tasks.js';

export function TaskSelectModal({ worker, onSelectTask, onClose, gameState }) {
  const audio = useAudio();
  const dialogRef = useRef(null);
  const firstTaskRef = useRef(null);

  // Play open sound when modal mounts
  useEffect(() => {
    audio.playSound('ui_open');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Open dialog when component mounts
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    // Show the dialog as a modal
    dialog.showModal();

    // Focus first task after dialog opens
    if (firstTaskRef.current) {
      firstTaskRef.current.focus();
    }

    const handleCancel = (e) => {
      // Cancel event fires when user presses Escape
      e.preventDefault(); // Prevent default close
      audio.playSound('ui_close');
      onClose(); // Call our close handler
    };

    const handleBackdropClick = (e) => {
      // Click on backdrop (outside dialog content)
      const rect = dialog.getBoundingClientRect();
      if (
        e.clientX < rect.left ||
        e.clientX > rect.right ||
        e.clientY < rect.top ||
        e.clientY > rect.bottom
      ) {
        audio.playSound('ui_close');
        onClose();
      }
    };

    // Listen for Escape key via cancel event (more reliable than 'close')
    dialog.addEventListener('cancel', handleCancel);
    dialog.addEventListener('click', handleBackdropClick);

    // Cleanup
    return () => {
      dialog.removeEventListener('cancel', handleCancel);
      dialog.removeEventListener('click', handleBackdropClick);
      // Don't call dialog.close() here - let unmounting handle it
    };
  }, [onClose]);

  // Get available tasks based on game state (buildings, etc.)
  const availableTasks = getAvailableTasks(gameState);

  // Convert to format expected by UI
  const tasks = availableTasks.map(taskDef => ({
    id: taskDef.id,
    label: taskDef.label,
    description: taskDef.description
  }));

  // Filter tasks based on worker status
  const filteredTasks = tasks.filter(task => {
    // Sick workers can only rest
    if (worker.status === 'sick') {
      return task.id === 'rest';
    }
    return true;
  });

  return (
    <dialog
      ref={dialogRef}
      className="backdrop:bg-black backdrop:bg-opacity-50 rounded-lg p-0 max-w-md"
    >
      <Card title={`Assign Worker ${worker.id}`} icon="📋">
        <div className="space-y-2">
          {filteredTasks.map((task, index) => (
            <button
              key={task.id}
              ref={index === 0 ? firstTaskRef : null}
              onClick={() => {
                audio.playSound('ui_confirm');
                audio.playSound('ui_close');
                onSelectTask(task.id);
              }}
              className="w-full p-3 bg-blue-600 hover:bg-blue-700 text-white rounded text-left transition-colors focus:ring-2 focus:ring-blue-300 focus:outline-none"
            >
              <div className="font-bold">{task.label}</div>
              <div className="text-sm text-blue-100">{task.description}</div>
            </button>
          ))}
        </div>
        <div className="mt-4 text-center">
          <Button onClick={() => {
            audio.playSound('ui_close');
            onClose();
          }}>Cancel</Button>
        </div>
      </Card>
    </dialog>
  );
}
